/**
 * character.ts
 * Character selection types for Starfall Courier.
 *
 * Each playable character has a unique sprite sheet, animation clips,
 * draw offset, and optional narrative voice. The physics hitbox is
 * identical across all characters — only visuals and flavour differ.
 *
 * @module character
 */

import type { NovaAnimState } from '../engine/gameState';
import type { SpriteSheet, AnimClip, DrawOffset } from '../rendering/SpriteRenderer';

/** Union of all playable character identifiers. */
export type CharacterId = 'nova' | 'avon' | 'belle';

/** Canonical tuple of all character identifiers — single source of truth. */
export const CHARACTER_IDS = ['nova', 'avon', 'belle'] as const;

/**
 * Measured content metrics for a character's IDLE frames, in raw sheet
 * pixels and relative to a single frame's top-left corner.
 *
 * The character-select preview blits the raw sheet as a CSS background and
 * therefore cannot run the runtime normaliser (`getNormalizedSheet`), which
 * is what equalises apparent character size during play. These measurements
 * let the preview reproduce that result: scaling by `contentHeight` makes
 * every character render at the same on-card height, and `centerX` /
 * `bottom` keep them centred on a shared floor line.
 *
 * Values are verified against the real PNGs by
 * `characterPreviewMetrics.test.ts` — update them if a sheet is repacked.
 */
export interface CharacterPreviewMetrics {
  /** Median height of the visible art across IDLE frames. */
  readonly contentHeight: number;
  /** Median horizontal centre of the visible art within its frame. */
  readonly centerX: number;
  /** Median exclusive bottom edge of the visible art within its frame. */
  readonly bottom: number;
}

/**
 * Static metadata for a playable character.
 * Consumed by the character-select screen and the sprite renderer.
 */
export interface CharacterMeta {
  /** Unique identifier. */
  readonly id: CharacterId;
  /** Display name shown on the character-select screen. */
  readonly name: string;
  /** One-line description for the character-select card. */
  readonly description: string;
  /** Sprite sheet descriptor (dimensions, frame size, frame count). */
  readonly sheet: SpriteSheet;
  /** Animation clip mapping for every movement/combat state. */
  readonly clips: Record<NovaAnimState, AnimClip>;
  /** Draw offset positioning the sprite relative to the physics hitbox. */
  readonly drawOffset: DrawOffset;
  /** Accent colour for the character-select card border/glow. */
  readonly accentColor: string;
  /** Measured IDLE content metrics driving the character-select preview. */
  readonly previewMetrics: CharacterPreviewMetrics;
}
